/**
 * Outbound “network” links for the site footer (same list as Desktop/info/dankore-websites-reference.md).
 * Update here when domains change.
 */
export type NetworkLink = {
  /** Stable id for utm_content */
  id: string;
  label: string;
  url: string;
  /** Shown first / slightly emphasized */
  hub?: boolean;
};

export const DANKORE_NETWORK_LINKS: readonly NetworkLink[] = [
  { id: 'yeswecan_hq', label: 'Yes We Can LLC', url: 'https://www.yeswecanhq.com/', hub: true },
  { id: 'dankoresoft', label: 'Dankore Software', url: 'https://www.dankoresoft.com/', hub: true },
  { id: 'logo_creator', label: 'Logo Creator', url: 'https://logo-creator.dankoresoft.com/' },
  { id: 'kadoverse', label: 'Kadoverse', url: 'https://www.kadoverse.dankoresoft.com/' },
  { id: 'taxnest', label: 'Taxnest', url: 'https://www.taxnest.ng/' },
  { id: 'halal_meat_share', label: 'Halal Meat Share', url: 'https://halalmeatshare.com/' },
  { id: 'halal_ads', label: 'Halal Ads', url: 'https://halal-ads.dankoresoft.com/' },
  { id: 'mentorship', label: 'Mentorship', url: 'https://mentorship.dankoresoft.com/' },
  { id: 'direct_lease', label: 'Direct Lease', url: 'https://direct-lease.dankoresoft.com/' },
  { id: 'bitcoin_street', label: 'Bitcoin Street', url: 'https://bitcoin-street.dankoresoft.com/' },
  { id: 'bitcoin_usd_price', label: 'Bitcoin USD Price', url: 'https://www.bitcoinusdprice.org/' },
  { id: 'bmi_calc', label: 'BMI Calculator', url: 'https://bmi-calc.dankoresoft.com/' },
  { id: 'mortgage_calc', label: 'Mortgage Calculator', url: 'https://mortgage-calc.dankoresoft.com/' },
  { id: 'ethnicity_calc', label: 'Ethnicity Calculator', url: 'https://ethnicity-calculator.dankoresoft.com/' },
  { id: 'race_calc', label: 'Race Calculator', url: 'https://race-calculator.dankoresoft.com/' },
  { id: 'nigerian_tax_id', label: 'Nigerian Tax ID Checker', url: 'https://nigerian-tax-id-checker.dankoresoft.com/' },
  { id: 'iowa_food_aid', label: 'Iowa Food Aid Finder', url: 'https://iowa-food-aid-finder.dankoresoft.com/' },
  { id: 'tinubu_rating', label: 'Tinubu approval rating', url: 'https://tinubu-approval-rating.dankoresoft.com/' },
] as const;

const LINK_BY_ID = new Map<string, NetworkLink>(DANKORE_NETWORK_LINKS.map((l) => [l.id, l]));

/** Multi-column footer grouping (SaaS-style). */
export const FOOTER_LINK_COLUMNS: readonly { title: string; ids: readonly string[] }[] = [
  { title: 'Company', ids: ['yeswecan_hq', 'dankoresoft'] },
  {
    title: 'Products & services',
    ids: [
      'logo_creator',
      'kadoverse',
      'taxnest',
      'halal_meat_share',
      'halal_ads',
      'mentorship',
      'direct_lease',
    ],
  },
  { title: 'Money & data', ids: ['bitcoin_street', 'bitcoin_usd_price', 'tinubu_rating'] },
  {
    title: 'Calculators & aid',
    ids: ['bmi_calc', 'mortgage_calc', 'ethnicity_calc', 'race_calc', 'nigerian_tax_id', 'iowa_food_aid'],
  },
] as const;

export function linksForFooterColumn(ids: readonly string[]): NetworkLink[] {
  return ids.map((id) => LINK_BY_ID.get(id)).filter((l): l is NetworkLink => l != null);
}

/** Referral params suggested in dankore-websites-reference.md */
export function withFooterReferralParams(url: string, utmContent: string): string {
  try {
    const u = new URL(url);
    u.searchParams.set('utm_source', 'yeswecan');
    u.searchParams.set('utm_medium', 'referral');
    u.searchParams.set('utm_campaign', 'footer');
    u.searchParams.set('utm_content', utmContent);
    return u.href;
  } catch {
    return url;
  }
}
